import { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import api from "@/lib/api";

const bubbleClass = (sender) => {
  if (sender === "user") return "ml-auto bg-brand text-[#06111C]";
  if (sender === "admin") return "mr-auto bg-white text-[#06111C]";
  return "mr-auto bg-surface2 text-[#22333F] border border-black/10";
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("umg_session"));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const scrollDown = () => setTimeout(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, 60);

  const start = useCallback(async () => {
    const { data } = await api.post("/chat/start", { name: "Visitor" });
    localStorage.setItem("umg_session", data.session_id);
    setSessionId(data.session_id);
    setMessages(data.messages);
    scrollDown();
  }, []);

  useEffect(() => {
    if (open && !sessionId) start();
  }, [open, sessionId, start]);

  useEffect(() => {
    if (!sessionId || !open) return;
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/chat/messages/${sessionId}`);
        if (!active) return;
        setMessages((prev) => (data.messages.length !== prev.length ? (scrollDown(), data.messages) : prev));
      } catch (e) {}
    };
    load();
    const iv = setInterval(load, 4000);
    return () => { active = false; clearInterval(iv); };
  }, [sessionId, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((p) => [...p, { id: `tmp-${Date.now()}`, sender: "user", text }]);
    scrollDown();
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId, text });
      setMessages((prev) => [...prev.filter((m) => !m.id.startsWith("tmp-")), ...data.messages]);
      scrollDown();
    } catch (e) {} finally { setSending(false); }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[95] flex flex-col items-end">
      <AnimatePresence>
        {open && (
          <motion.div data-testid="chat-window"
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }} transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="mb-4 w-[92vw] max-w-sm h-[70vh] max-h-[520px] bg-surface border border-black/10 flex flex-col shadow-2xl origin-bottom-right">
            <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
              <div>
                <p className="font-display font-bold text-base text-[#0E1E2E]">UMG Media Help</p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-brand flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-brand inline-block rounded-full animate-pulse" /> Online
                </p>
              </div>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-[#556673] hover:text-[#0E1E2E]">
                <X size={18} />
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[82%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${bubbleClass(m.sender)}`}>
                  {m.sender === "admin" && <span className="block font-mono2 text-[9px] uppercase tracking-widest opacity-70 mb-1">UMG Team</span>}
                  {m.text}
                </div>
              ))}
              {sending && <div className="mr-auto text-[#8797A3] font-mono2 text-xs">typing…</div>}
            </div>
            <form onSubmit={send} className="border-t border-black/10 p-3 flex items-center gap-2">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our shows or ebooks…"
                className="flex-1 bg-transparent text-sm text-[#0E1E2E] placeholder:text-[#8797A3] py-2" />
              <button data-testid="chat-send-btn" type="submit" className="w-9 h-9 bg-brand text-[#06111C] flex items-center justify-center hover:bg-[#27B95B] transition-colors">
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <button data-testid="chat-toggle-btn" aria-label="Open chat" onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 bg-brand text-[#06111C] flex items-center justify-center shadow-2xl hover:scale-105 transition-transform">
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>
    </div>
  );
};
